import { useState, useCallback, useMemo } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import {
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useShowCreditsPaywall } from 'hooks/useShowCreditsPaywall'
import { RefineSettings } from './components/RefineSettings'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import type { Provider } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { usePerformRegeneration } from './hooks/usePerformRegeneration'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'

export type MediaSourceOverride = 'ai_video' | 'ai_image' | 'default'

const getMediaSourceOverride = (provider: string | undefined): MediaSourceOverride => {
  if (provider === 'veo3') return 'ai_video'
  if (provider === 'ai_generated') return 'ai_image'
  return 'default'
}

interface RegenerateDesignModalProps {
  almanacFile: AlmanacFileShowItem
}

const HIDDEN_PROVIDERS_DEFAULT: Provider[] = ['gifs', 'blazeRecommendations', 'stockVideo']
const HIDDEN_PROVIDERS_AI_GENERATED: Provider[] = ['stockVideo', 'stockPhoto', 'gifs', 'blazeRecommendations']
const HIDDEN_PROVIDERS_VIDEO: Provider[] = ['gifs', 'blazeRecommendations', 'stockPhoto', 'ai']

export const RegenerateDesignModal = ({ almanacFile }: RegenerateDesignModalProps) => {
  const { closeModal, openModal } = useModals()
  const { usageCosts, creditsRemaining, canAutoReload } = useCreditUsage()
  const { price, isTrackingCredits } = useShowCreditsPaywall()
  const sourceMediaInfo = almanacFile.source_media_info

  const [topicOverride, setTopicOverride] = useState<string | null>(null)
  const [sourceMediaOverride, setSourceMediaOverride] = useState<ImageMenuSelectedImage | null>(null)
  const [error, setError] = useState<string | null>(null)

  const topic = topicOverride ?? sourceMediaInfo?.topic ?? ''
  const displaySourceMediaUrl = sourceMediaOverride?.url ?? sourceMediaInfo?.source_url ?? ''

  const isAiGenerated = sourceMediaInfo?.provider === 'ai_generated' || sourceMediaInfo?.provider === 'veo3'
  const isSourceVideo = sourceMediaInfo?.mime_type?.startsWith('video/')

  const { performRegeneration, isLoading: isRegenerating } = usePerformRegeneration({
    designId: almanacFile.key,
    isAiGenerated,
    onSuccess: closeModal,
    onError: setError,
  })

  const isLoading = isRegenerating

  const imageMenuHiddenProviders = useMemo((): Provider[] => {
    if (isAiGenerated) {
      return HIDDEN_PROVIDERS_AI_GENERATED
    }
    if (isSourceVideo) {
      return HIDDEN_PROVIDERS_VIDEO
    }
    return HIDDEN_PROVIDERS_DEFAULT
  }, [isAiGenerated, isSourceVideo])

  const generationCost = useMemo(() => {
    switch (sourceMediaInfo?.provider) {
      case 'veo3':
        return usageCosts?.video_credits_cost
      case 'ai_generated':
        return usageCosts?.visual_styles_credits_cost
      default:
        return isTrackingCredits ? usageCosts?.content_credits_cost : 0
    }
  }, [sourceMediaInfo?.provider, usageCosts, isTrackingCredits])

  const hasInsufficientCredits = creditsRemaining < (generationCost ?? 0) && !!price && !canAutoReload

  const handlePerformRegeneration = useCallback(() => {
    const mediaFileKey = sourceMediaOverride?.id ?? sourceMediaInfo?.key ?? null
    const mediaSourceOverride = getMediaSourceOverride(sourceMediaInfo?.provider)
    setError(null)
    performRegeneration({ mediaFileKey, topic, mediaSourceOverride })
  }, [sourceMediaOverride, sourceMediaInfo, topic, performRegeneration])

  const handleRegenerate = useCallback(() => {
    if (hasInsufficientCredits) {
      openModal(
        PurchaseContentCreditsModal,
        {
          price,
          onSuccess: handlePerformRegeneration,
          defaultBatchCount: Math.ceil((generationCost ?? 0) / 100) || 1,
        },
        { newStack: true },
      )
      return
    }

    handlePerformRegeneration()
  }, [hasInsufficientCredits, generationCost, price, openModal, handlePerformRegeneration])

  const handleReplaceImage = useCallback(() => {
    const accept = isSourceVideo && !isAiGenerated ? ACCEPTED_VIDEO_MIME_TYPES : ACCEPTED_IMAGE_MIME_TYPES_NO_SVG
    openModal(
      ImageMenuModal,
      {
        accept,
        hiddenProviders: imageMenuHiddenProviders,
        maxCount: 1,
        uploadMedia: true,
        title: 'Select Source',
        onUse: ([image]: ImageMenuSelectedImage[]) => {
          if (image) {
            setSourceMediaOverride(image)
          }
        },
      },
      { newStack: true },
    )
  }, [openModal, imageMenuHiddenProviders, isSourceVideo, isAiGenerated])

  return (
    <Modal.Root size="lg" onPressOutside={() => {}}>
      <RefineSettings
        topic={topic}
        setTopic={setTopicOverride}
        sourceMediaUrl={displaySourceMediaUrl}
        error={error}
        generationCost={generationCost ?? 0}
        isLoading={isLoading}
        onClose={closeModal}
        onRegenerate={handleRegenerate}
        onReplaceImage={handleReplaceImage}
        provider={sourceMediaInfo?.provider}
        hasInsufficientCredits={hasInsufficientCredits}
      />
    </Modal.Root>
  )
}
